import dayjs, { Dayjs, OpUnitType } from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { Button, Dropdown, Spin, theme } from "antd";
import {
  cloneElement,
  PropsWithChildren,
  ReactElement,
  useMemo,
  useRef,
  useState,
} from "react";
import { Flex } from "src/components/Flex";
import { useSubscribeEffect } from "src/hooks/useSubscribeEffect";
import { nextRange, previousDuration, previousRange } from "src/utils/date";
import { useClickAnyWhere, useHover } from "usehooks-ts";
import { useDurationOrDatesToRange } from "src/hooks/useDurationOrDatesToRange";
import { DynamicCanvas } from "src/components/DynamicCanvas";
import { hasParent } from "src/utils/dom";
import { useUniqueId } from "src/hooks/useUniqueId";
import { Hover } from "src/components/Hover";
import { SearchActions } from "src/search/types";
import { EntitySearchTimeSeries } from "./useEntityContext";

dayjs.extend(utc);

function BarTooltip({
  value,
  payload,
  label,
  className,
  previous,
  onRangeStart,
  onRangeClear,
  onRangeChange,
  timeUnit,
  searchContext,
}: {
  value: any;
  payload: Dayjs;
  label: any;
  className: string;
  previous: [string, Dayjs];
  timeUnit: string;
  onRangeStart(): void;
  onRangeClear(): void;
  onRangeChange?: (dates: [string, string]) => void;
  searchContext: EntitySearchTimeSeries;
}) {
  const { token } = theme.useToken();
  const { params } = searchContext;
  const { dispatch } = params;
  return (
    <Flex
      className={className}
      gap={6}
      style={{
        pointerEvents: "all",
        backgroundColor: token.colorBgElevated,
        borderRadius: token.borderRadius,
        fontSize: 12,
        boxShadow: token.boxShadowTertiary,
        paddingRight: 3,
        paddingTop: 3,
      }}
    >
      <div style={{ position: "relative" }}>
        <Dropdown
          key={label}
          placement="bottomLeft"
          menu={{
            items: [
              {
                key: "goto",
                label: (
                  <span style={{ fontSize: 12 }}>
                    Go to <strong>{label}</strong>
                  </span>
                ),
                onClick() {
                  dispatch({
                    type: SearchActions.setDates,
                    value: [
                      payload.startOf(timeUnit as OpUnitType).toISOString(),
                      payload.endOf(timeUnit as OpUnitType).toISOString(),
                    ],
                  });
                  onRangeChange &&
                    onRangeChange([
                      payload.startOf(timeUnit as OpUnitType).toISOString(),
                      payload.endOf(timeUnit as OpUnitType).toISOString(),
                    ]);
                },
              },
              {
                key: "range",
                label: previous ? (
                  <span style={{ fontSize: 12 }}>
                    Limit between <strong>{label}</strong> -{" "}
                    <strong>{previous[0]}</strong>
                  </span>
                ) : (
                  <span style={{ fontSize: 12 }}>Start Range Selection</span>
                ),
                onClick() {
                  if (previous) {
                    const d1 = payload;
                    const d2 = previous[1];
                    const rev = d1.isAfter(d2);
                    const start = (rev ? d2 : d1).startOf(
                      timeUnit as OpUnitType
                    );
                    const end = (rev ? d1 : d2).endOf(timeUnit as OpUnitType);
                    dispatch({
                      type: SearchActions.setDates,
                      value: [start.toISOString(), end.toISOString()],
                    });
                    onRangeClear();
                  } else {
                    onRangeStart();
                  }
                },
              },
              {
                key: "clear",
                onClick: onRangeClear,
                disabled: !previous,
                label: (
                  <span style={{ fontSize: 12 }}>Clear Range Selection</span>
                ),
              },
            ].filter((o) => !o.disabled),
          }}
          mouseLeaveDelay={1.5}
          dropdownRender={(menu) => (
            <div
              style={{
                borderRadius: token.borderRadiusLG,
                backgroundColor: token.colorBgElevated,
                boxShadow: token.boxShadow,
              }}
            >
              {cloneElement(menu as ReactElement, {
                style: { boxShadow: "none" },
              })}
            </div>
          )}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              paddingLeft: 3,
              paddingBottom: 3,
            }}
          >
            <Hover
              baseStyle={{
                display: "inline-flex",
                backgroundColor: `${token.colorFillSecondary}`,
                color: `${token.colorText}`,
                borderRadius: token.borderRadiusSM,
                alignItems: "center",
                cursor: "pointer",
                fontSize: 12,
                gap: 4,
                fontWeight: 500,
              }}
              hoverStyle={{
                backgroundColor: `${token.colorPrimary}77`,
                color: `${token.colorWhite}`,
              }}
            >
              <div className="trunc">
                <Flex
                  className={className}
                  gap={6}
                  style={{ padding: "4px 8px" }}
                >
                  <strong>{value}</strong>
                  <span>({label})</span>
                </Flex>
              </div>
            </Hover>
          </div>
        </Dropdown>
      </div>
    </Flex>
  );
}

export function TimeSeries(props: {
  resizeTriggerKey?: string;
  height?: number;
  showFilterMenus?: boolean;
  searchContext: EntitySearchTimeSeries;
  shape: "bar" | "scatter";
  showXAxis?: boolean;
  cursorPosition?: number | null;
  onRangeChange?: (dates: [string, string]) => void;
  onMouseMove?: (data: any, index: number) => void;
  onActiveChange?: (active: boolean) => void;
}) {
  const { token } = theme.useToken();
  const { params, timeSeries } = props.searchContext;
  const data = useTimeSeriesData({
    data: timeSeries.data?.data || [],
    timeUnit: ["days", "hours", "minutes"].includes(timeSeries.timeUnit)
      ? (timeSeries.timeUnit as "days" | "hours" | "minutes")
      : "days",
    start: params.state.startDate,
    end: params.state.endDate,
    duration: params.state.duration,
  });
  useSubscribeEffect(timeSeries);
  const [isActive, setActive] = useState(false);
  const id = useUniqueId();
  useClickAnyWhere((e: MouseEvent) => {
    if (
      (e.target as SVGElement).id !== id &&
      !hasParent(e.target as HTMLElement, `svg#${id}`) &&
      !hasParent(e.target as HTMLElement, `*.${id}`)
    ) {
      setActive(false);
      props.onActiveChange && props.onActiveChange(false);
    }
  });

  const [prev, setPrev] = useState<any>();

  return (
    <Flex key={`resize-event-${props.resizeTriggerKey || ""}`} centered gap={4}>
      <Flex centered style={{ flex: 1, height: props.height }}>
        <DynamicCanvas>
          {({ width, height }) => (
            <div style={{ height: "100%", position: "relative" }}>
              {timeSeries.isLoading ? (
                <Spin
                  style={{ marginLeft: "calc(50% - 20px)", marginTop: "20px" }}
                />
              ) : (
                <BarChart
                  id={id}
                  width={width}
                  height={height}
                  data={data}
                  className="time-series-chart"
                  onClick={() => {
                    setActive(true);
                    props.onActiveChange && props.onActiveChange(true);
                  }}
                  onMouseMove={props.onMouseMove}
                >
                  <XAxis
                    axisLine={false}
                    tick={{
                      fontSize: 11,
                      fontWeight: 600,
                      fill: token.colorTextTertiary,
                    }}
                    height={20}
                    dataKey="0"
                    hide={!props.showXAxis}
                  />
                  <YAxis hide />
                  <Tooltip
                    trigger={isActive ? "click" : "hover"}
                    wrapperStyle={{
                      visibility: isActive ? "visible" : "hidden",
                    }}
                    content={({ active, payload, label }) => {
                      if (isActive && active && payload && payload.length > 0) {
                        return (
                          <BarTooltip
                            timeUnit={timeSeries.timeUnit}
                            previous={prev}
                            payload={payload[0].payload[2]}
                            className={id}
                            value={payload[0].value}
                            label={label}
                            onRangeStart={() => {
                              setPrev([
                                payload[0].payload[0],
                                payload[0].payload[2],
                              ]);
                            }}
                            onRangeChange={props.onRangeChange}
                            onRangeClear={() => {
                              setPrev(undefined);
                            }}
                            searchContext={props.searchContext}
                          />
                        );
                      }
                    }}
                    contentStyle={{
                      border: "none",
                      borderRadius: token.borderRadius,
                      opacity: 0.95,
                    }}
                  />
                  <Bar
                    animationBegin={0}
                    animationDuration={0}
                    dataKey="1"
                    fill={token.colorFillSecondary}
                    shape={({ x, y, width, height }) => {
                      if (props.shape === "bar") {
                        return (
                          <rect
                            x={x}
                            y={y + height - Math.max(6, height)}
                            width={width}
                            height={Math.max(6, height)}
                            fill={
                              height === 0
                                ? token.colorFillSecondary
                                : token.colorPrimary
                            }
                          />
                        );
                      } else {
                        //height is 30 when it's 1.
                        const radius = Math.max(height / 10, 3); // Set the radius of the circle to half the height of the bar
                        return (
                          <circle
                            cx={x + width / 2}
                            cy={35} //half of the height of the bar chart
                            r={radius}
                            fill={
                              height === 0
                                ? token.colorFillSecondary
                                : token.colorPrimary
                            }
                          />
                        );
                      }
                    }}
                  />
                  {props.cursorPosition && props.cursorPosition > 0 && (
                    <line
                      x1={props.cursorPosition}
                      y1={0}
                      x2={props.cursorPosition}
                      y2={300}
                      stroke={token.colorPrimary}
                      strokeWidth={1}
                      strokeDasharray="5 5"
                    />
                  )}
                </BarChart>
              )}
              {prev && (
                <Flex
                  start
                  centered
                  gap={8}
                  style={{
                    position: "absolute",
                    backgroundColor: token.colorPrimaryBg,
                    width: "90%",
                    left: "5%",
                    bottom: -10,
                    borderRadius: token.borderRadiusSM,
                    paddingLeft: 8,
                    fontSize: 12,
                    opacity: 0.9,
                    boxShadow: `0 1px 2px ${token.colorPrimaryBorder}`,
                  }}
                >
                  <div style={{ flex: 1, lineHeight: 2 }}>
                    Click to select another date
                  </div>
                  <Button
                    style={{
                      fontSize: 12,
                      textShadow: `0 0 2px ${token.colorWhite}`,
                    }}
                    type="text"
                    size="small"
                    onClick={() => setPrev(undefined)}
                  >
                    <CloseOutlined /> Cancel
                  </Button>
                </Flex>
              )}
            </div>
          )}
        </DynamicCanvas>
      </Flex>
    </Flex>
  );
}

export function LeftNavButton(props: {
  searchContext: EntitySearchTimeSeries;
  onRangeChange?: (dates: [string, string]) => void;
}) {
  const { params } = props.searchContext;
  return (
    <NavButton
      onClick={() => {
        if (params.state.duration) {
          const value = previousDuration(params.state.duration);
          if (props.onRangeChange) {
            props.onRangeChange([value.startDate, value.endDate]);
          } else {
            params.dispatch({
              type: SearchActions.setDates,
              value: [value.startDate, value.endDate],
            });
          }
        } else if (params.state.endDate && params.state.startDate) {
          const value = previousRange(
            params.state.startDate,
            params.state.endDate
          );
          if (props.onRangeChange) {
            props.onRangeChange([value.startDate, value.endDate]);
          } else {
            params.dispatch({
              type: SearchActions.setDates,
              value: [value.startDate, value.endDate],
            });
          }
        }
      }}
    >
      <ArrowLeftOutlined />
    </NavButton>
  );
}

export function RightNavButton(props: {
  searchContext: EntitySearchTimeSeries;
  onRangeChange?: (dates: [start: string, end: string]) => void;
}) {
  const { params } = props.searchContext;
  return (
    <NavButton
      onClick={() => {
        if (params.state.endDate && params.state.startDate) {
          const value = nextRange(params.state.startDate, params.state.endDate);
          if (props.onRangeChange) {
            props.onRangeChange([value.startDate, value.endDate]);
          } else {
            params.dispatch({
              type: SearchActions.setDates,
              value: [value.startDate, value.endDate],
            });
          }
        }
      }}
    >
      <ArrowRightOutlined />
    </NavButton>
  );
}

function NavButton(props: PropsWithChildren<{ onClick(): void }>) {
  const rightButtonRef = useRef<HTMLDivElement | null>(null);
  const rightHover = useHover(rightButtonRef);
  const { token } = theme.useToken();
  return (
    <div ref={rightButtonRef}>
      <Button
        style={{
          height: "100%",
          padding: 5,
          backgroundColor: rightHover
            ? token.colorFillSecondary
            : token.colorFillTertiary,
        }}
        type="text"
        onClick={props.onClick}
      >
        {props.children}
      </Button>
    </div>
  );
}

export function useTimeSeriesData(
  props: PropsWithChildren<{
    data: [string, number][];
    duration?: string;
    start?: string;
    end?: string;
    timeUnit: "days" | "hours" | "minutes";
  }>
): Array<[string, number, Dayjs]> {
  const [start, end] = useDurationOrDatesToRange(props);
  return useMemo(() => {
    const diff = Math.abs(start.diff(end, props.timeUnit));
    const formats = {
      days: "MMM DD",
      minutes: "ddd H:m",
      hours: "ddd ha",
    };
    const reformattedMap = Object.fromEntries(
      props.data.map((xx) => [
        dayjs
          .utc(xx[0].replace(/Z\[UTC]/, ""))
          .local()
          .format(formats[props.timeUnit]),
        xx[1],
      ])
    );
    return new Array(diff + 1).fill(0).map((_, i) => {
      const label = start
        .add(i, props.timeUnit)
        .format(formats[props.timeUnit]);
      return [
        label,
        reformattedMap[label] || 0,
        start.add(i, props.timeUnit).local(),
      ];
    });
  }, [props.data, start, end, props.timeUnit]);
}
